/**
 * TaskDrawer Component
 *
 * Main container component for the task detail drawer.
 * Orchestrates sub-components and manages data flow.
 *
 * Architecture:
 * - Decomposed from original TaskDrawerFull.js (1,159 lines)
 * - Uses custom hooks for state management
 * - Sub-components handle specific UI regions
 *
 * Accessibility:
 * - Focus trapped within drawer when open
 * - Escape key closes drawer
 * - ARIA dialog pattern with proper labelling
 * - Screen reader announcements for state changes
 *
 * Mobile:
 * - Skips drawer entirely and navigates to full page
 * - Better UX than 100% width drawer overlay
 *
 * @see WCAG 2.1 AA - 2.4.7 Focus Visible
 */

import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Drawer, Modal, Form, message } from 'antd';
import AppSpinner from '../shared/AppSpinner';
import { CloseOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';

// Context hooks
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useUserContext } from '../../../../../core/components/users/useUserContext';
import { useOrgContext } from '../../../../../core/components/orgs/useOrgContext';

// Sub-components
import TaskDrawerHeader from './TaskDrawerHeader';
import TaskDrawerContent from './TaskDrawerContent';
import TaskDrawerSidebar from './TaskDrawerSidebar';

// Hooks
import useResponsiveTask from '../../hooks/useResponsiveTask';
import { useDrawerState } from './hooks/useDrawerState';
import { icon } from '../../configs/designTokens';

// Styles
import '../../styles/index.scss';

/**
 * Calculate drawer width based on viewport
 * @param {number} width - Viewport width
 * @returns {string|number} Drawer width
 */
const getDrawerWidth = (width) => {
  if (width < 768) return '100%';
  if (width < 1200) return '90%';
  return '70%';
};

const TaskDrawer = ({ visible, onClose, taskId, onTaskUpdate }) => {
  // Form instance
  const [form] = Form.useForm();
  const commentFormRef = useRef(null);

  // Focus management refs
  const previouslyFocusedElement = useRef(null);
  const drawerRef = useRef(null);

  // Context hooks
  const {
    currentUser,
    fetchTask,
    currentTask,
    updateTask,
    updateTaskLoading,
    fetchHistories,
    histories,
    loadingHistories,
  } = useTaskContext();

  const {
    fetchBasicUsersDetails,
    basicUsersDetails,
    basicUsersDetailsLoading,
    fetchCollaboratorsUsers,
    collaboratorsData,
    collaboratorsLoading,
  } = useUserContext();

  const {
    fetchBasicOrgsDetails,
    basicOrgsDetails,
    basicOrgsDetailsLoading,
  } = useOrgContext();

  // Responsive hook
  const { width: windowWidth, isMobile } = useResponsiveTask();

  // Router hook for mobile redirect
  const history = useHistory();

  // Mobile: Skip drawer and navigate directly to full page
  useEffect(() => {
    if (visible && isMobile && taskId) {
      // Close the drawer and navigate to full page
      onClose();
      history.push(`/tasks/details/${taskId}`);
    }
  }, [visible, isMobile, taskId, onClose, history]);

  // Focus management: Save focus when opening, restore when closing
  useEffect(() => {
    if (visible) {
      // Save the currently focused element before drawer opens
      previouslyFocusedElement.current = document.activeElement;
    } else if (previouslyFocusedElement.current) {
      // Restore focus when drawer closes
      previouslyFocusedElement.current.focus?.();
      previouslyFocusedElement.current = null;
    }
  }, [visible]);

  // Handle focus after drawer animation completes
  const handleAfterOpenChange = useCallback((open) => {
    if (open && drawerRef.current) {
      // Focus the first focusable element in the drawer (close button or title)
      const firstFocusable = drawerRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        // Small delay to ensure DOM is ready
        requestAnimationFrame(() => {
          firstFocusable.focus();
        });
      }
    }
  }, []);

  // Drawer state hook
  const {
    isEditingAssignee,
    setIsEditingAssignee,
    activeTab,
    setActiveTab,
    historiesFetched,
    setHistoriesFetched,
    showUnsavedModal,
    checkUnsavedComments,
    executeWithUnsavedCheck,
    handleConfirmUnsavedChanges,
    handleCancelUnsavedChanges,
    isFollowers,
    isCollaborator,
    isCurrentUserAssignee,
    level,
    resetState,
  } = useDrawerState({
    taskId,
    currentTask,
    currentUser,
    commentFormRef,
  });

  // Memoized debounced fetch functions
  const fetchBasicUsers = useMemo(
    () =>
      debounce((name = '') => {
        const selectedOrg = form.getFieldValue('assigneeOrg');
        if (selectedOrg === null) {
          form.setFieldValue('assigneeUsers', []);
          return;
        }
        fetchBasicUsersDetails({
          variables: {
            first: 100,
            skip: 0,
            where: {
              AND: [
                {
                  roles_some: {
                    org: {
                      id: selectedOrg,
                      level: 'PLATFORM',
                    },
                    roleItems_some: {
                      feature: 'TASK_MANAGEMENT',
                    },
                  },
                  name_contains: name,
                },
              ],
            },
          },
        });
      }, 500),
    [form, fetchBasicUsersDetails]
  );

  const fetchCollaborators = useMemo(
    () =>
      debounce((name = '') => {
        if (!currentUser?.role?.org?.id) return;
        fetchCollaboratorsUsers({
          variables: {
            first: 100,
            skip: 0,
            where: {
              AND: [
                {
                  roles_some: {
                    org: {
                      OR: [
                        { parentOrg_some: { id: currentUser.role.org.id } },
                        { id: currentUser.role.org.id },
                      ],
                      level: 'PLATFORM',
                    },
                    roleItems_some: {
                      feature: 'TASK_MANAGEMENT',
                    },
                  },
                  name_contains: name,
                },
              ],
            },
          },
        });
      }, 500),
    [currentUser.role.org.id, fetchCollaboratorsUsers]
  );

  const fetchBasicOrgs = useMemo(
    () =>
      debounce((name = '') => {
        if (!currentUser?.role?.org?.id) return;
        fetchBasicOrgsDetails({
          variables: {
            first: 10,
            skip: 0,
            where: {
              AND: [
                {
                  OR: [
                    { parentOrg_some: { id: currentUser.role.org.id } },
                    { id: currentUser.role.org.id },
                  ],
                  name_contains: name,
                },
              ],
              OR: [
                {
                  roles_some: {
                    org: { level: 'PLATFORM' },
                    roleItems_some: { feature: 'TASK_MANAGEMENT' },
                  },
                },
              ],
            },
          },
        });
      }, 500),
    [currentUser.role.org.id, fetchBasicOrgsDetails]
  );

  // Fetch task data when drawer opens
  useEffect(() => {
    if (visible && taskId) {
      fetchTask({ variables: { where: { id: taskId } } });
      fetchCollaborators();
      // Fetch histories immediately for tab count display
      fetchHistories({
        variables: {
          where: {
            referenceId: taskId,
            type: 'TASK',
          },
        },
      });
      setHistoriesFetched(true);
    } else if (!visible) {
      resetState();
    }
  }, [visible, taskId, fetchTask, fetchCollaborators, fetchHistories, setHistoriesFetched, resetState]);

  // Update form when task data changes
  useEffect(() => {
    if (currentTask) {
      form.setFieldsValue({
        status: currentTask.status,
        assigneeOrg: currentTask.assigneeOrg?.id || null,
        assigneeUsers: currentTask.assigneeUsers?.[0]?.id || undefined,
        collaborators: currentTask.collaborators?.map((user) => user.id) || [],
      });
    }
  }, [currentTask, form]);

  // Handle drawer close with unsaved check
  const handleClose = useCallback(() => {
    executeWithUnsavedCheck(() => {
      onClose();
    });
  }, [executeWithUnsavedCheck, onClose]);

  // Handle status change
  const handleStatusChange = useCallback(
    (newStatus) => {
      executeWithUnsavedCheck(async () => {
        try {
          await updateTask({
            variables: {
              data: { status: newStatus },
              where: { id: taskId },
            },
          });
          await fetchTask({ variables: { where: { id: taskId } } });
          message.success('Task status updated successfully.');
          if (onTaskUpdate) {
            onTaskUpdate();
          }
        } catch (error) {
          console.error('Error updating status:', error);
          message.error('Failed to update task status.');
        }
      });
    },
    [executeWithUnsavedCheck, updateTask, taskId, fetchTask, onTaskUpdate]
  );

  // Don't render if not visible or on mobile (mobile redirects to full page)
  if (!visible || isMobile) {
    return null;
  }

  // Loading state
  if (!currentTask) {
    return (
      <Drawer
        title="Loading Task..."
        placement="right"
        onClose={handleClose}
        width={getDrawerWidth(windowWidth)}
        open={visible}
        maskClosable={true}
        className="task-drawer-full"
        aria-label="Loading task details"
        aria-busy="true"
      >
        <div className="task-drawer-full__loading" role="status">
          <AppSpinner size="large" tip="Loading task details..." />
          <span className="sr-only">Loading task details, please wait</span>
        </div>
      </Drawer>
    );
  }

  return (
    <>
      <Drawer
        title={null}
        placement="right"
        onClose={handleClose}
        width={getDrawerWidth(windowWidth)}
        open={visible}
        maskClosable={true}
        className="task-drawer-full"
        closeIcon={<CloseOutlined style={{ fontSize: icon.md }} />}
        aria-labelledby="task-drawer-title"
        aria-describedby="task-drawer-description"
        role="dialog"
        aria-modal="true"
        footer={null}
        afterOpenChange={handleAfterOpenChange}
      >
        {/* Wrapper for focus management */}
        <div ref={drawerRef}>
        {/* Screen reader description */}
        <span id="task-drawer-description" className="sr-only">
          Task details panel for {currentTask.name}. Use Tab to navigate through sections.
        </span>

        {/* Header */}
        <TaskDrawerHeader
          currentTask={currentTask}
          taskId={taskId}
        />

        {/* Main Content */}
        <div className="task-drawer-full__content">
          {/* Left: Tabs Content */}
          <TaskDrawerContent
            currentTask={currentTask}
            taskId={taskId}
            currentUser={currentUser}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            commentFormRef={commentFormRef}
            onTaskUpdate={onTaskUpdate}
            histories={histories}
            loadingHistories={loadingHistories}
            fetchHistories={fetchHistories}
            historiesFetched={historiesFetched}
            setHistoriesFetched={setHistoriesFetched}
          />

          {/* Right: Sidebar */}
          <TaskDrawerSidebar
            currentTask={currentTask}
            taskId={taskId}
            currentUser={currentUser}
            form={form}
            level={level}
            isEditingAssignee={isEditingAssignee}
            setIsEditingAssignee={setIsEditingAssignee}
            isFollowers={isFollowers}
            isCollaborator={isCollaborator}
            isCurrentUserAssignee={isCurrentUserAssignee}
            updateTaskLoading={updateTaskLoading}
            updateTask={updateTask}
            fetchTask={fetchTask}
            onTaskUpdate={onTaskUpdate}
            executeWithUnsavedCheck={executeWithUnsavedCheck}
            onStatusChange={handleStatusChange}
            fetchBasicOrgs={fetchBasicOrgs}
            fetchBasicUsers={fetchBasicUsers}
            fetchCollaborators={fetchCollaborators}
            basicOrgsDetails={basicOrgsDetails}
            basicOrgsDetailsLoading={basicOrgsDetailsLoading}
            basicUsersDetails={basicUsersDetails}
            basicUsersDetailsLoading={basicUsersDetailsLoading}
            collaboratorsData={collaboratorsData}
            collaboratorsLoading={collaboratorsLoading}
          />
        </div>
        </div>
      </Drawer>

      {/* Unsaved Changes Modal */}
      <Modal
        title="Unsaved Changes"
        open={showUnsavedModal}
        onOk={handleConfirmUnsavedChanges}
        onCancel={handleCancelUnsavedChanges}
        okText="Continue"
        cancelText="Cancel"
        centered
        aria-labelledby="unsaved-modal-title"
      >
        <p id="unsaved-modal-title">
          You have unsaved changes in your comment. Do you want to continue?
        </p>
        <p className="task-drawer-full__modal-note">
          Note: Your unsaved comment will be lost if you proceed.
        </p>
      </Modal>
    </>
  );
};

TaskDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  taskId: PropTypes.string,
  onTaskUpdate: PropTypes.func,
};

TaskDrawer.defaultProps = {
  taskId: null,
  onTaskUpdate: null,
};

export default TaskDrawer;
